import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Heart, Instagram, Search, X, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import api from '@/lib/api';
import { getWishlist } from '@/pages/Favoritos';
import { useAppData } from '@/context/AppDataContext';

// Nova logo Carvalho Achados
const LOGO_URL = "https://customer-assets.emergentagent.com/job_instant-site-pass/artifacts/oznhzql0_image.png";

const navLinks = [
  { href: '/', label: 'Inicio' },
  { href: '/ofertas', label: 'Ofertas' },
  { href: '/virais', label: 'Virais' },
  { href: '/categorias', label: 'Categorias' },
];

const SHOPEE_LINK = "https://collshp.com/carvalhocomercio?view=storefront";
const TEMU_LINK = "https://temu.to/k/gssc2z9fkh6";
const WHATSAPP_LINK = "https://whatsapp.com/channel/0029Vb7oBZaKbYMPWBZkWb2i";
const INSTAGRAM_LINK = "https://www.instagram.com/carvalhoachados/";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Usa o contexto global em vez de fazer fetch próprio
  const { blogCount } = useAppData();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Track wishlist count
  useEffect(() => {
    const update = () => setWishlistCount(getWishlist().length);
    update();
    window.addEventListener('wishlist-updated', update);
    return () => window.removeEventListener('wishlist-updated', update);
  }, []);

  // Close results on route change
  useEffect(() => {
    setShowResults(false);
    setSearchQuery('');
  }, [location.pathname]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Palavras comuns em português que não devem ser buscadas sozinhas
  const commonWords = ['de', 'da', 'do', 'das', 'dos', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem', 'que', 'um', 'uma', 'os', 'as', 'ao', 'e'];

  // Função para ordenar resultados por relevância
  const sortByRelevance = (products, query) => {
    const q = query.toLowerCase().trim();
    return [...products].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      
      // Prioridade 1: Nome começa exatamente com o termo
      const aStartsWithTerm = nameA.startsWith(q);
      const bStartsWithTerm = nameB.startsWith(q);
      if (aStartsWithTerm && !bStartsWithTerm) return -1;
      if (!aStartsWithTerm && bStartsWithTerm) return 1;
      
      // Prioridade 2: Alguma palavra do nome começa com o termo
      const wordsA = nameA.split(/[\s\-,]+/);
      const wordsB = nameB.split(/[\s\-,]+/);
      const aWordStartsWith = wordsA.some(w => w.startsWith(q));
      const bWordStartsWith = wordsB.some(w => w.startsWith(q));
      if (aWordStartsWith && !bWordStartsWith) return -1;
      if (!aWordStartsWith && bWordStartsWith) return 1;
      
      return 0;
    });
  };

  // Filtra resultados para mostrar apenas os relevantes
  const filterRelevantResults = (products, query) => {
    const q = query.toLowerCase().trim();
    
    // Se a busca é uma palavra comum, retorna vazio
    if (commonWords.includes(q)) {
      return [];
    }
    
    // Filtra para mostrar apenas produtos onde uma palavra começa com o termo
    return products.filter(p => {
      const name = p.name.toLowerCase();
      const words = name.split(/[\s\-,.()+]+/);
      // Pelo menos uma palavra deve começar com o termo buscado
      return words.some(w => w.startsWith(q));
    });
  };

  // Real-time search with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = searchQuery.trim().toLowerCase();
    
    // Não busca se for muito curto ou palavra comum
    if (!q || q.length < 2 || commonWords.includes(q)) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get('/products', { params: { search: searchQuery.trim(), limit: 50 } });
        // Filtra resultados relevantes, ordena por relevância e limita a 8
        const relevantResults = filterRelevantResults(response.data || [], searchQuery.trim());
        const sortedResults = sortByRelevance(relevantResults, searchQuery.trim()).slice(0, 8);
        setSearchResults(sortedResults);
        setShowResults(sortedResults.length > 0);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/ofertas?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  const allNavLinks = blogCount > 0
    ? [...navLinks, { href: '/blog', label: 'Blog' }]
    : navLinks;

  return (
    <header
      className="fixed top-[36px] left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-[0_2px_15px_rgba(0,0,0,0.06)]"
      data-testid="header"
    >
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center gap-3 h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={LOGO_URL} alt="Carvalho Comercio" className="h-10 md:h-12 w-auto object-contain" />
          </Link>

          {/* Search Bar - Desktop */}
          <div ref={searchRef} className="hidden md:block flex-1 max-w-2xl mx-4 relative">
            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.trim() && setShowResults(true)}
                  placeholder="Buscar produtos, ofertas, categorias..."
                  className="w-full h-10 pl-10 pr-24 bg-slate-50/80 border border-slate-200 rounded-full text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/10 transition-all text-sm"
                  data-testid="header-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setShowResults(false); }}
                    className="absolute right-20 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <Button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#FF4500] hover:bg-[#E03D00] text-white rounded-full px-6 h-8 text-xs font-bold shadow-sm"
                >
                  Buscar
                </Button>
              </div>
            </form>

            {/* Search Results Dropdown */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 max-h-[400px] overflow-y-auto">
                {searching ? (
                  <div className="p-4 text-center text-slate-400 text-sm">Buscando...</div>
                ) : searchResults.length > 0 ? (
                  <div className="p-2">
                    {searchResults.map((product) => (
                      <Link
                        key={product.id}
                        to={`/ofertas?q=${encodeURIComponent(product.name.split(' ').slice(0, 3).join(' '))}`}
                        onClick={() => setShowResults(false)}
                        className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-xl transition-colors"
                      >
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover bg-slate-100 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800 truncate">{product.name}</p>
                          <p className="text-xs text-orange-600 font-semibold">{formatPrice(product.current_price)}</p>
                        </div>
                        {product.discount_percent > 0 && (
                          <span className="text-xs bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">
                            -{product.discount_percent}%
                          </span>
                        )}
                      </Link>
                    ))}
                    <button
                      onClick={() => {
                        navigate(`/ofertas?q=${encodeURIComponent(searchQuery)}`);
                        setShowResults(false);
                      }}
                      className="w-full text-center p-3 text-sm text-orange-600 font-medium hover:bg-orange-50 rounded-xl transition-colors mt-1"
                    >
                      Ver todos os resultados
                    </button>
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-400 text-sm">
                    Nenhum resultado para "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 flex-shrink-0" data-testid="desktop-nav">
            {allNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                  isActive(link.href)
                    ? 'bg-orange-50 text-[#FF4500] font-bold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Grupo VIP WhatsApp - Desktop */}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1DA851] text-white px-3.5 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105 shadow-sm"
              data-testid="header-whatsapp-link"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Grupo VIP
            </a>

            {/* Vitrine Shopee - Desktop */}
            <a
              href={SHOPEE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 bg-[#EE4D2D] hover:bg-[#D93A1E] text-white px-3.5 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105 shadow-sm"
              data-testid="header-shopee-link"
            >
              <Store className="w-3.5 h-3.5" />
              Shopee
            </a>

            {/* Temu - Desktop */}
            <a
              href={TEMU_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 bg-[#FB6D28] hover:bg-[#E55A1B] text-white px-3.5 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105 shadow-sm"
              data-testid="header-temu-link"
            >
              <Store className="w-3.5 h-3.5" />
              Temu
            </a>

            <div className="hidden md:block">
              <a
                href={INSTAGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-slate-500 hover:text-[#E4405F] transition-colors inline-block"
                data-testid="instagram-link"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>

            {/* Mobile Search Toggle + Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-slate-100">
                    <img src={LOGO_URL} alt="Carvalho Comercio" className="h-10 w-auto object-contain" />
                  </div>

                  {/* Mobile Search */}
                  <div className="p-4 border-b border-slate-100">
                    <form onSubmit={(e) => { handleSearchSubmit(e); setMobileMenuOpen(false); }}>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Buscar ofertas..."
                          className="w-full h-11 pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-full text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-orange-500 text-sm"
                          data-testid="mobile-search-input"
                        />
                      </div>
                    </form>
                  </div>

                  <nav className="flex-1 p-4" data-testid="mobile-nav">
                    <div className="space-y-1">
                      {allNavLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                            isActive(link.href)
                              ? 'bg-orange-50 text-orange-600'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                      <Link
                        to="/favoritos"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          location.pathname === '/favoritos'
                            ? 'bg-orange-50 text-orange-600'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Heart className="w-4 h-4 mr-2" />
                        Favoritos {wishlistCount > 0 && `(${wishlistCount})`}
                      </Link>
                      <a
                        href={SHOPEE_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center px-4 py-3 rounded-lg text-base font-bold text-white bg-[#EE4D2D] hover:bg-[#D93A1E] transition-colors mt-2"
                        data-testid="mobile-shopee-link"
                      >
                        <Store className="w-4 h-4 mr-2" />
                        Shopee
                      </a>
                      <a
                        href={TEMU_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center px-4 py-3 rounded-lg text-base font-bold text-white bg-[#FB6D28] hover:bg-[#E55A1B] transition-colors mt-2"
                        data-testid="mobile-temu-link"
                      >
                        <Store className="w-4 h-4 mr-2" />
                        Temu
                      </a>
                      <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center px-4 py-3 rounded-lg text-base font-bold text-white bg-[#25D366] hover:bg-[#1DA851] transition-colors mt-2"
                        data-testid="mobile-whatsapp-link"
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Grupo VIP WhatsApp
                      </a>
                    </div>
                  </nav>

                  <div className="p-4 border-t border-slate-100">
                    <div className="flex items-center justify-center gap-4">
                      <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-[#E4405F] transition-colors" aria-label="Instagram">
                        <Instagram className="w-6 h-6" />
                      </a>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
